import { W, H, LAUNCHER_Y, R, Phase, Button } from './types';
import { getButtons, getPauseButtons } from './renderer';

export interface InputHandlers {
  getPhase: () => Phase;
  isMenuOpen: () => boolean;
  aimAt: (x: number, y: number) => void;
  fire: () => void;
  pressButton: (name: string) => void;
}

function inside(b: Button, x: number, y: number): boolean {
  return x >= b.x && x <= b.x + b.w && y >= b.y && y <= b.y + b.h;
}

export function attachInput(canvas: HTMLCanvasElement, h: InputHandlers): void {
  const toLogical = (clientX: number, clientY: number) => {
    const r = canvas.getBoundingClientRect();
    return { x: (clientX - r.left) * (W / r.width), y: (clientY - r.top) * (H / r.height) };
  };

  const hitButton = (x: number, y: number): boolean => {
    const btns = getButtons(h.getPhase());
    for (const name in btns) {
      if (inside(btns[name], x, y)) { h.pressButton(name); return true; }
    }
    return false;
  };

  let aiming = false;

  canvas.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    const { x, y } = toLogical(e.clientX, e.clientY);
    if (h.getPhase() === 'playing' && h.isMenuOpen()) {
      // Paused menu intercepts every tap — never reaches the launcher.
      const btns = getPauseButtons();
      let hit = false;
      for (const name in btns) {
        if (inside(btns[name], x, y)) { h.pressButton(name); hit = true; break; }
      }
      if (!hit) h.pressButton('resume'); // tap outside the panel → close
      aiming = false;
      return;
    }
    if (hitButton(x, y)) { aiming = false; return; }
    if (h.getPhase() === 'playing' && y < LAUNCHER_Y - R) {
      aiming = true;
      h.aimAt(x, y);
      canvas.setPointerCapture(e.pointerId);
    }
  }, { passive: false });

  canvas.addEventListener('pointermove', (e) => {
    if (h.getPhase() !== 'playing' || h.isMenuOpen()) return;
    const { x, y } = toLogical(e.clientX, e.clientY);
    h.aimAt(x, y);   // aim follows pointer (desktop hover + touch drag)
  }, { passive: false });

  canvas.addEventListener('pointerup', (e) => {
    e.preventDefault();
    if (aiming && h.getPhase() === 'playing' && !h.isMenuOpen()) h.fire();
    aiming = false;
  }, { passive: false });

  canvas.addEventListener('pointercancel', () => { aiming = false; });

  window.addEventListener('keydown', (e) => {
    if (h.getPhase() !== 'playing') {
      if (e.key === 'r' || e.key === 'R') h.pressButton('again');
      return;
    }
    if (h.isMenuOpen()) {
      if (e.key === 'Escape') h.pressButton('resume');
      return;
    }
    if (e.key === ' ') { e.preventDefault(); h.fire(); }
    else if (e.key === 'r' || e.key === 'R') h.pressButton('restart');
    else if (e.key === 'Escape') h.pressButton('menu');
  });
}
